import ThirdPartyDisabledModal from 'components/ThirdPartyDisabledModal'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import Order from 'state/models/Order'
import { useReseller } from 'state/Reseller'
import tinycolor from 'tinycolor2'
import { isReddish, klass, platform, tooDark, useEffectOnce } from 'utils'
import Alerts from '../alerts'
import './layout.scss'

// window.ENV = { COLOR: '#F8B61D' } //eHungry
// window.ENV = { COLOR: '#CD2027' } //CMO
// window.ENV = { COLOR: '#301D17' } //Generic
// window.ENV = undefined            //Web

export default function Page({ routes }) {
	const pathParts = useLocation().pathname.split('/')
	const pathKey = pathParts[1]

	const pageClass = (Object.values(Order.STATUS).indexOf(pathKey) >= 0 || !pathKey)?
		'orders' :
		pathParts.filter(v => v && v.match(/^\D*$/)).join('-')

	//region Reseller branding
	const reseller      = useReseller()
	const resellerStyle = reseller?.style
	const htmlEl        = document.querySelector('html')

	/*
	 * Color logic should be:
	 * 1. login screen? ALWAYS clear styles, which defaults to brown or the native app color
	 * 2. Logged in:
	 *   2A) not classified yet?
	 *     2A1) has reseller?
	 *       2A1A) has styles? use them and mark as classified
	 *       2A1B) no styles? use default and mark as classified
	 *     2A2) no reseller? use yellow and mark as classified
	 *   2B) already classified: do nothing
	 * Note: it's not possible to NOT reference brown/yellow here; we need that to define if the theme is light or dark
	 */

	//path search instead of isAuth() since we already use the path here, and this is the only place without auth anyway
	if (pathKey == 'login') {
		clearCustomStyles()
		//no need to classify the HTML tag yet, we'll do it once the user logs in
	} else if (!htmlEl.dataset.classified) { //simple way to avoid running this multiple times - must be removed at logout
		htmlEl.dataset.classified = 'yes'
		if (reseller) {
			if (resellerStyle.primary) {
				setCustomStyles(resellerStyle.primary, resellerStyle.secondary, resellerStyle.logo)
			} else {
				setClasses(false) //just does color classification; uses the standard background from CSS, NOT from app
			}
		} else {
			setCustomStyles('#F8B61D')
		}
	}
	//endregion

	//And now it's safe to hide the native splash screen, we're about to mount the UI :)
	//The fade takes 500ms by default, and can be changed with pref FadeSplashScreenDuration
	useEffectOnce(() => {
		if ('splashscreen' in navigator) {
			navigator.splashscreen.hide()
		}
	})

	return <div id={`${pageClass}-page`}>
		<ThirdPartyDisabledModal/> {/* FIXME this is overly specific to be on the main Page's structure */}
		{routes}
		<Alerts/>
	</div>
}

Page.propTypes = {
	routes: PropTypes.element.isRequired,
}

function setClasses(primary) {
	if (!primary) {
		primary = getComputedStyle(document.querySelector(':root')).getPropertyValue('--palette-primary')
	}

	const darkBg = tooDark(primary)
	const redBg  = isReddish(primary)

	document.querySelector('html').className = klass({
		android: platform.isAndroid(),
		ios    : platform.isIOS(),
		web    : platform.isWeb(),
		native : platform.isNative(),

		//these classes mean we're using a reseller palette, so we got to use readable text colors
		// accordingly (black/white), and  take out any remainders from eHungry that are not covered by
		// the CSS variables above. Check the SASS mixins "brandless-rules" and "bg-aware-text".
		redBg,
		darkBg,
		lightBg: !darkBg,
	}).className
}

function setCustomStyles(primary, secondary = null, logo = null) {
	const htmlEl = document.querySelector('html')

	//TODO: maybe revisit those "darker" variants, since they may end up as black if the reseller's primary is dark
	const dark   = tinycolor(primary).darken(7).toString()
	const darker = tinycolor(dark).darken(4).toString()

	htmlEl.style.setProperty(`--palette-primary`, primary)
	htmlEl.style.setProperty(`--palette-primary-dark`, dark)
	htmlEl.style.setProperty(`--palette-primary-darker`, darker)

	if (secondary) {
		htmlEl.style.setProperty('--palette-secondary', secondary)
	}
	if (logo) {
		if (logo['1x'] && logo['2x']) {
			htmlEl.style.setProperty('--logo', `image-set(
				"${logo['1x']}" 1x,
				"${logo['2x']}" 2x
			)`)
		} else if (logo['1x']) {
			htmlEl.style.setProperty('--logo', `url("${logo['1x']}")`)
		} else {
			htmlEl.style.setProperty('--logo', `url("${logo}")`) //never used? but better safe than sorry lol
		}
	}

	setClasses(primary)
}

function clearCustomStyles() {
	const htmlEl = document.querySelector('html')

	htmlEl.classList.remove('red-bg', 'dark-bg', 'light-bg')

	if (window.ENV?.COLOR) { //this is coming from from #49933. Probably good to leave this if-else as safeguard
		setCustomStyles(window.ENV.COLOR)
	} else {
		htmlEl.style.removeProperty('--palette-primary')
		htmlEl.style.removeProperty('--palette-primary-dark')
		htmlEl.style.removeProperty('--palette-primary-darker')
	}

	htmlEl.style.removeProperty('--palette-secondary')
	htmlEl.style.removeProperty('--logo')

	delete htmlEl.dataset.classified //causes the reseller styles to be re-check every time <Page> re-renders
}
